/**
 * MhiCart — shared "add to cart" helper.
 *
 * One place that owns the post-add behaviour driven by the theme's
 * `settings.cart_type` (exposed as window.themeSettings.cartType):
 *   - "drawer"       → re-render and open <cart-drawer>
 *   - "notification" → patch the cart-notification sections and open it
 *   - "page"         → redirect to the cart page
 *
 * Usage:
 *   MhiCart.add(
 *     [{ id: variantId, quantity: 2 }],
 *     {
 *       onError:   function (message) { ... },   // optional
 *       onSuccess: function (data)    { ... },    // optional, NOT called for "page" (redirects)
 *     }
 *   );
 *
 * Returns the fetch promise so callers can hook .finally() for loading state.
 */
(function () {
  if (window.MhiCart) return;

  function getCartType() {
    var cartType = window.themeSettings && window.themeSettings.cartType;
    return ['drawer', 'page', 'notification'].indexOf(cartType) === -1 ? null : cartType;
  }

  // Resolve the live cart element for the active cart type, with a sensible fallback.
  function getCartElement(cartType) {
    if (cartType === 'drawer') return document.querySelector('cart-drawer');
    if (cartType === 'notification') return document.querySelector('cart-notification');
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  /**
   * When the cart was empty, <cart-drawer>/<cart-notification> carry the
   * `is-empty` class and CSS hides the items panel. Strip it before rendering,
   * same as Shopify's product-form.js does in its finally block.
   */
  function stripIsEmpty(el) {
    if (el && el.classList.contains('is-empty')) el.classList.remove('is-empty');
  }

  /**
   * Shopify bulk add returns { items: [...], sections: {...} }.
   * cart-notification.renderContents needs a single-item shape with a top-level `key`.
   * cart-drawer.renderContents only needs `sections`, so it works with both shapes.
   * Normalise to the single-item shape to keep both happy.
   */
  function normalizeResponse(data) {
    if (data.items && !data.key) {
      return Object.assign({}, data.items[0], { sections: data.sections });
    }
    return data;
  }

  /**
   * cart-notification.renderContents looks up 'cart-notification-product-{key}'
   * in the current DOM. For bulk adds the response carries multiple new items
   * whose keys were never in the DOM, so getElementById returns null → crash.
   * Bypass renderContents for cart-notification and patch the section nodes
   * directly, then call open() to reveal it.
   */
  function renderCartElement(el, data) {
    if (el.tagName && el.tagName.toLowerCase() === 'cart-drawer') {
      el.renderContents(normalizeResponse(data));
      return;
    }

    var sections = data.sections || {};
    var parser = new DOMParser();

    ['cart-notification-product', 'cart-notification-button'].forEach(function (id) {
      if (!sections[id]) return;
      var parsed = parser.parseFromString(sections[id], 'text/html');
      var src = parsed.getElementById('shopify-section-' + id);
      var dest = document.getElementById('shopify-section-' + id);
      if (src && dest) dest.innerHTML = src.innerHTML;
    });

    if (sections['cart-icon-bubble']) {
      var bParsed = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
      var bSrc = bParsed.getElementById('cart-icon-bubble') || bParsed.getElementById('shopify-section-cart-icon-bubble');
      var bDest = document.getElementById('cart-icon-bubble') || document.getElementById('shopify-section-cart-icon-bubble');
      if (bSrc && bDest) bDest.innerHTML = bSrc.innerHTML;
    }

    if (typeof el.open === 'function') el.open();
  }

  // Reveal the cart UI (drawer/notification) after a successful add.
  function showCart(cartType, data) {
    var cartEl = getCartElement(cartType);

    // Element may have registered after page load — wait for it, then re-query.
    if (!cartEl || typeof cartEl.renderContents !== 'function') {
      var fallbackName = cartType === 'drawer' ? 'cart-drawer' : 'cart-notification';
      customElements.whenDefined(fallbackName).then(function () {
        var el = document.querySelector(fallbackName);
        if (el && typeof el.renderContents === 'function') {
          stripIsEmpty(el);
          renderCartElement(el, data);
        } else {
          window.location = window.routes.cart_url;
        }
      });
      return;
    }

    stripIsEmpty(cartEl);
    renderCartElement(cartEl, data);
  }

  function add(items, options) {
    options = options || {};
    var onError = typeof options.onError === 'function' ? options.onError : function () {};
    var onSuccess = typeof options.onSuccess === 'function' ? options.onSuccess : function () {};

    if (!items || !items.length) {
      onError('Please select at least one item.');
      return Promise.resolve(null);
    }

    var cartType = getCartType();

    // Collect the sections the active cart UI needs so it can re-render in place.
    var sections = [];
    var cartEl = getCartElement(cartType);
    if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
      sections = cartEl.getSectionsToRender().map(function (s) {
        return s.id;
      });
    }

    var body = { items: items };
    if (sections.length) {
      body.sections = sections.join(',');
      body.sections_url = window.location.pathname;
    }

    return fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/javascript',
      },
      body: JSON.stringify(body),
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        if (data.status) {
          onError(data.description || data.message || 'Error adding to cart.');
          return;
        }

        if (cartType === 'page') {
          window.location = window.routes.cart_url;
          return;
        }

        showCart(cartType, data);
        onSuccess(data);
      })
      .catch(function (err) {
        console.error('MhiCart.add error:', err);
        onError('Something went wrong. Please try again.');
      });
  }

  window.MhiCart = { add: add };
})();
